import isNil from "lodash-es/isNil";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import weui from "weui.js";
import AlipayIcon from "../../assets/image/alipay.png";
import UnionPayIcon from "../../assets/image/unionpay.png";
import WechatPayIcon from "../../assets/image/wechatpay.png";
import {
  Button,
  Cell,
  CellBody,
  CellDescription,
  CellFooter,
  CellHeader,
  Cells,
  CellsTitle,
  Label,
  Loadmore,
  Preview,
  PreviewBody,
  PreviewHead,
  PreviewItem,
  Radio,
  Select
} from "../../components/weui";

import { payService, userService } from "../../services";
import { orderService } from "../../services/orderService";
import { refreshBanksOfB2b, selectBanksOfB2b } from "../../stores";
import { urls } from "../../urls";
import { PayChannels, formatCurrency, formatDateTime, getAxiosError, isWechat } from "../../utils";

/** 通过支付方式名称获取对应的图标, 目前仅支持微信, 支付宝和银联 */
const getPayIcon = name => {
  if (name.indexOf("微信") !== -1) return WechatPayIcon;
  else if (name.indexOf("支付宝") !== -1) return AlipayIcon;
  else if (name.indexOf("网银") !== -1 || name.indexOf("银联") !== -1) return UnionPayIcon;
  else return null;
};
/** 获取支付方式描述, 主要区分微信 */
const getPayDesc = id => {
  let str = null;
  let wxStr = "仅支持在微信内使用";
  let notWxStr = "不支持微信内使用, 请使用手机自带浏览器打开此页面";
  switch (id) {
  case 1://微信支付
    str = wxStr;
    break;
  case 2://支付宝
    str = notWxStr;
    break;
  case 3://微信支付HF
    str = wxStr;
    break;
  case 4://支付宝HF
    str = notWxStr;
    break;
  case 5://企业网银
    str = notWxStr;
    break;
  default:
    break;
  }
  return str;
};

export const PayChannel = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orderNo } = useParams();
  // 订单信息
  const [order, setOrder] = useState();
  // 支付方式列表
  const [channels, setChannels] = useState([]);
  // 当前选中支付方式
  const [selectedCh, setSelectedCh] = useState(0);
  // 公对公支付银行列表
  const BankCodings = useSelector(selectBanksOfB2b);
  // 当前选中银行
  const [bankId, setBankId] = useState();
  // 是否可支付
  const canPay = useMemo(() => order && order.orderState === "未支付" && channels.some(ch => ch.id === selectedCh), [order, channels, selectedCh]);

  // 加载订单信息
  const reload = useCallback(async () => {
    const loading = weui.loading("加载中");
    try {
      // 加载订单数据
      const res = await userService.getOrderDetail(orderNo);
      setOrder(res.data);
      if (!isNil(res.data.bankType)) {
        setBankId(res.data.bankType);
      } else {
        if (BankCodings.length > 0) {
          setBankId(BankCodings[0].code);
        }
      }

      // 检查订单状态
      let errMsg;
      switch (res.data.orderState) {
      case "未支付":
        errMsg = null;
        break;
      case "等待加电":
        errMsg = "订单已支付";
        break;
      case "已关闭":
      case "已完成":
      default:
        errMsg = `订单${res.data.orderState}`;
        break;
      }

      if (errMsg !== null) {
        weui.alert(errMsg, () => navigate(urls.paymentDetail(orderNo)), { title: "无法支付" });
        return;
      }

      // 加载支付方式
      const resChn = await orderService.getPayChannel(res.data.meterNo);
      if (isWechat()) {
        //微信环境 排除汇付支付宝 企业网银
        resChn.data = resChn.data.filter(p => p.id !== PayChannels.AlipayHF && p.id !== PayChannels.B2BHF);
      } else {
        //非微信环境 排除微信支付
        resChn.data = resChn.data.filter(p => p.id !== PayChannels.TenPay && p.id !== PayChannels.TenPayHF);
      }
      if (resChn.data.length < 1) {
        weui.alert("无有效支付方式", () => navigate(urls.fee()), { title: "无法支付" });
        return;
      }
      setChannels(resChn.data);
      setSelectedCh(res.data.payChannel || resChn.data[0].id);
    } catch (err) {
      weui.alert(getAxiosError(err), () => navigate(urls.fee()), { title: "订单加载失败" });
    } finally {
      loading.hide();
    }
  }, [orderNo, setOrder, navigate, BankCodings]);

  useEffect(() => {
    if (BankCodings.length === 0) {
      dispatch(refreshBanksOfB2b());
    }

    reload();
  }, [BankCodings.length, dispatch, reload]);

  const goPay = useCallback(async () => {
    const loading = weui.loading("处理中");
    try {
      // 支付宝二维码支付跳转支付宝 App 的 URL Scheme
      const alipayScanCodePrefix = "alipays://platformapi/startapp?saId=10000007&qrcode=";
      let res, alipayUrl;

      // 将 form 插入 body 并立即提交, 用于支付宝手机页面支付及企业网银支付跳转.
      const insertAndSubmitForm = formHtml => {
        const elId = "elFormWrapper";
        let el = document.getElementById(elId);
        if (!el) {
          el = document.createElement("div");
          el.id = elId;
          el.style = "display:none";
          document.body.appendChild(el);
        }
        el.innerHTML = formHtml;
        el.getElementsByTagName("form").item(0).submit();
      };

      // 1_微信, 2_支付宝, 3_微信HF, 4_支付宝HF, 5_公对公HF
      switch (selectedCh) {
      case PayChannels.TenPay: // 微信
      case PayChannels.TenPayHF: // 汇付微信
        // 获取微信用户认证跳转地址
        res = await payService.getWechatAuthUrl(orderNo, `${selectedCh}`);
        // 跳转进行微信用户认证及后续支付操作
        window.location.href = res.data;
        break;
      case PayChannels.Alipay: // 支付宝
        // 调用支付宝手机网页支付下单, 返回值为 form
        res = await payService.alipay(orderNo);
        // 插入并提交 form
        insertAndSubmitForm(res.data);
        break;
      case PayChannels.AlipayHF: // 汇付支付宝
        // 汇付支付宝下单, 返回值为支付宝二维码 Url
        res = await payService.alipayNSPOS(orderNo);
        // 拼接地址
        alipayUrl = `${alipayScanCodePrefix}${res.data}`;
        // 跳转至支付宝 App 内完成支付
        window.location.href = alipayUrl;
        break;
      case PayChannels.B2BHF: // 汇付公对公
        // 汇付公对公支付下单, 返回值为 form
        res = await payService.b2bNSPOS(orderNo, bankId);
        // 插入并提交 form
        insertAndSubmitForm(res.data);
        break;
      default:
        weui.alert("暂不支持此支付方式, 请选择其它方式支付.");
        break;
      }
    } catch (err) {
      weui.topTips(getAxiosError(err));
    } finally {
      loading.hide();
    }
  }, [orderNo, bankId, selectedCh]);

  return (
    <>
      <Preview className="mb-5">
        <PreviewHead>
          <PreviewItem label="订单金额">{ formatCurrency(order?.totalMoney) } 元</PreviewItem>
        </PreviewHead>
        <PreviewBody>
          <PreviewItem label="订单编号">{ order?.orderNo }</PreviewItem>
          <PreviewItem label="表计编号">{ order?.meterNo } [{ order?.meterClass }]</PreviewItem>
          <PreviewItem label="所属区域">{ order?.corpName }</PreviewItem>
          <PreviewItem label="订单时间">{ order ? formatDateTime(new Date(order?.createTime)) : "" }</PreviewItem>
        </PreviewBody>
      </Preview>

      <CellsTitle>请选择您的支付方式</CellsTitle>

      {
        channels.length === 0
          ? <Loadmore>加载中...</Loadmore>
          : <Cells radio>
            {
              // 微信支付/支付宝/企业网银
              channels.map(ch => (
                <Cell key={ ch.id } htmlFor={ `chr${ch.id}` } className={ { "weui-cell_disabled": !isNil(order.payChannel) && order.payChannel !== ch.id } } radio>
                  <CellHeader><img src={ getPayIcon(ch.name) } alt="图标" className="w-10 mr-5" /></CellHeader>
                  <CellBody>
                    { ch.name }
                    <CellDescription>{ getPayDesc(ch.id) }</CellDescription>
                  </CellBody>
                  <CellFooter>
                    <Radio id={ `chr${ch.id}` }
                      name="radio"
                      checked={ ch.id === selectedCh }
                      disabled={ !isNil(order.payChannel) && order.payChannel !== ch.id }
                      onChange={ e => setSelectedCh(ch.id) } />
                  </CellFooter>
                </Cell>
              ))
            }
          </Cells>
      }
      {
        selectedCh === PayChannels.B2BHF &&
          <>
            <CellsTitle>请选择您的支付银行</CellsTitle>
            <Cells>
              <Cell htmlFor="elBanks" selectAfter>
                <CellHeader>
                  <Label>支付银行</Label>
                </CellHeader>
                <CellBody>
                  <Select id="elBanks" disabled={ !isNil(order.bankType) } value={ bankId } onChange={ e => setBankId(e.target.value) }>
                    {
                      BankCodings.map(b => <option key={ b.code } value={ b.code }>{ b.name }</option>)
                    }
                  </Select>
                </CellBody>
              </Cell>
            </Cells>
          </>
      }
      <div className="mt-10">
        <Button type="primary" disabled={ !canPay } onClick={ e => goPay() }>立即支付</Button>
      </div>
    </>
  );
};
